// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * `_accumulatingTier` — resolves the dashboard's **A-tier** (continuously
 * accumulating) figures from the two reads that carry them: the polled
 * progress payload (`CycleProgress`, fresh at the 5 s tick) and the
 * once-per-iteration `CycleSnapshotDto` (PRD §Further Notes K/A/F; slice
 * `dashboard-live-values`, US §5–§8, §13).
 *
 * Fallback ladder (whole-tier, not per-field): a progress row supersedes
 * the snapshot for every A-tier value; with no progress row the snapshot's
 * values stand in (older cycles predating the emitter); with neither, each
 * value is `null` and the panel renders "—" (never a fabricated `0`).
 *
 * F-tier values (scores, fitness, leaderboard, eval reliability) are NOT
 * resolved here — they stay snapshot-only, so no progress value can leak
 * into a final-tier panel (the A/F split is visible in this seam).
 *
 * Pure: already-parsed payloads in, a normalised view-model out — no fetch
 * (INV-3).
 */

import { isFiniteNumber } from "@/lib/number-guards"
import type { CycleProgress } from "./_progressPayload"

export interface AccumulatingTier {
  /** Aggregate live spend (`spend.total`) — the budget-ring source. */
  spent: number | null
  /** Evaluation-category spend (`evaluation_spend.total`) — the eval leg of the split. */
  evalSpend: number | null
  /** Meta-category spend (`architect_spend.total + genetic_spend.total`) — the meta leg; `null` only when neither leg is a finite number. */
  metaSpend: number | null
  promptTokens: number | null
  completionTokens: number | null
  /** `prompt + completion` when both are known, else `null`. */
  totalTokens: number | null
  totalCalls: number | null
  /** Intra-iteration counter numerator — progress-only (no snapshot mirror). */
  agentsEvaluated: number | null
  /** Intra-iteration counter denominator — progress-only. */
  expectedPopulation: number | null
  /** Which read the values came from — makes the A/F split assertable. */
  source: "progress" | "snapshot" | "none"
}

const finiteOrNull = (value: unknown): number | null =>
  isFiniteNumber(value) ? value : null

const sumTokens = (
  prompt: number | null,
  completion: number | null,
): number | null =>
  prompt != null && completion != null ? prompt + completion : null

// Meta = architect + genetic. Either leg missing counts as 0, but the
// whole leg is `null` only when both are — so an all-absent meta renders
// "—", never a fabricated $0 (mirrors `deriveSpendBreakdown`'s meta merge).
const sumMeta = (
  architect: number | null,
  genetic: number | null,
): number | null =>
  architect == null && genetic == null
    ? null
    : (architect ?? 0) + (genetic ?? 0)

const fromProgress = (progress: CycleProgress): AccumulatingTier => {
  const promptTokens = finiteOrNull(progress.prompt_tokens)
  const completionTokens = finiteOrNull(progress.completion_tokens)
  return {
    spent: finiteOrNull(progress.spend?.total),
    evalSpend: finiteOrNull(progress.evaluation_spend?.total),
    metaSpend: sumMeta(
      finiteOrNull(progress.architect_spend?.total),
      finiteOrNull(progress.genetic_spend?.total),
    ),
    promptTokens,
    completionTokens,
    totalTokens: sumTokens(promptTokens, completionTokens),
    totalCalls: finiteOrNull(progress.total_llm_calls),
    // Per-iteration numerator (latest-wins), paired with the iteration's
    // expected population — NOT the cumulative `total_agents_evaluated`, which
    // would mix cumulative-over-per-iteration ("748/28") in the rendered pair.
    agentsEvaluated: finiteOrNull(progress.agents_evaluated_this_iter),
    expectedPopulation: finiteOrNull(progress.expected_population_size),
    source: "progress",
  }
}

const fromSnapshot = (snapshot: CycleSnapshotDto): AccumulatingTier => {
  const spend = snapshot.spend
  // The snapshot only carries the in/out token split when the ComputeEngine
  // capture succeeded (`split_available`); otherwise tokens are unknown, not 0.
  const splitOk = spend?.split_available === true
  const promptTokens = splitOk ? finiteOrNull(spend?.prompt_tokens) : null
  const completionTokens = splitOk
    ? finiteOrNull(spend?.completion_tokens)
    : null
  // The ledger's `n_calls` is authoritative; `total_llm_calls` has been
  // observed stuck at 0 while the ledger recorded the real calls.
  const totalCalls =
    finiteOrNull(spend?.n_calls) ?? finiteOrNull(snapshot.total_llm_calls)
  return {
    spent: finiteOrNull(spend?.total),
    evalSpend: finiteOrNull(snapshot.evaluation_spend?.total),
    metaSpend: sumMeta(
      finiteOrNull(snapshot.architect_spend?.total),
      finiteOrNull(snapshot.genetic_spend?.total),
    ),
    promptTokens,
    completionTokens,
    totalTokens: sumTokens(promptTokens, completionTokens),
    totalCalls,
    // The snapshot has no evaluated / expected-population counter — that
    // intra-iteration progress is A-tier only.
    agentsEvaluated: null,
    expectedPopulation: null,
    source: "snapshot",
  }
}

const EMPTY: AccumulatingTier = {
  spent: null,
  evalSpend: null,
  metaSpend: null,
  promptTokens: null,
  completionTokens: null,
  totalTokens: null,
  totalCalls: null,
  agentsEvaluated: null,
  expectedPopulation: null,
  source: "none",
}

export const selectAccumulatingTier = (
  progress: CycleProgress | null,
  snapshot: CycleSnapshotDto | null,
): AccumulatingTier => {
  if (progress != null) return fromProgress(progress)
  if (snapshot != null) return fromSnapshot(snapshot)
  return EMPTY
}
